import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Grid, Paper, Typography, Tabs, Tab, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PageHeader } from '../../components/ui/PageHeader';
import { VehicleMap } from '../../components/gps/VehicleMap';
import { DataTable } from '../../components/common/DataTable';
import { gpsAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useToast } from '../../contexts/ToastContext';

export const GPSTrackingDetailPage: React.FC = () => {
  const { vehicleId = '' } = useParams();
  const navigate = useNavigate();
  const { formatDate } = useLocale();
  const { showToast } = useToast();
  const [tab, setTab] = useState(0);
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [trips, setTrips] = useState<Record<string, unknown>[]>([]);
  const [history, setHistory] = useState<Record<string, unknown>[]>([]);
  const [playbackTrip, setPlaybackTrip] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [locRes, tripsRes, histRes] = await Promise.all([
        gpsAPI.getVehicleLocation(vehicleId),
        gpsAPI.getVehicleTrips(vehicleId),
        gpsAPI.getLocationHistory(vehicleId, { limit: '200' }),
      ]);
      setDetail(locRes.data);
      setTrips(tripsRes.data || []);
      setHistory(histRes.data || []);
    } catch {
      showToast('Failed to load vehicle tracking', 'error');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, showToast]);

  useEffect(() => { load(); }, [load]);

  const vehicle = detail?.vehicle as Record<string, unknown> | undefined;
  const location = detail?.location as Record<string, unknown> | undefined;
  const driver = detail?.driver as Record<string, unknown> | undefined;
  const client = vehicle?.client as Record<string, unknown> | undefined;
  const device = vehicle?.gpsDevice as Record<string, unknown> | undefined;

  const routePoints = playbackTrip
    ? ((playbackTrip.routePoints as Array<{ latitude: number; longitude: number }>) || []).map((p) => ({
        lat: p.latitude,
        lng: p.longitude,
      }))
    : location?.latitude
      ? [{ lat: location.latitude as number, lng: location.longitude as number }]
      : [];

  const handlePlayback = async (tripId: string) => {
    try {
      const res = await gpsAPI.getTripPlayback(tripId);
      setPlaybackTrip(res.data);
      setTab(0);
    } catch {
      showToast('Failed to load route playback', 'error');
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/gps/live')} sx={{ mb: 1 }}>
        Back to Dashboard
      </Button>

      <PageHeader
        title={String(vehicle?.vehicleNumber || 'Vehicle Tracking')}
        subtitle={`${vehicle?.vehicleType || ''} · ${client?.clientName || 'No customer assigned'}`}
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Status', value: String(location?.movementStatus || 'offline') },
          { label: 'Speed', value: `${location?.speed ?? 0} km/h` },
          { label: 'GPS', value: String(location?.onlineStatus || 'offline') },
          { label: 'Driver', value: String(driver?.driverName || '—') },
          { label: 'IMEI', value: String(device?.imeiNumber || '—') },
          { label: 'Provider', value: String(device?.provider || '—') },
        ].map((item) => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <VehicleMap
        markers={location?.latitude ? [{
          id: vehicleId,
          lat: location.latitude as number,
          lng: location.longitude as number,
          label: String(vehicle?.vehicleNumber),
          status: String(location?.movementStatus),
        }] : []}
        route={routePoints.length > 1 ? routePoints : []}
        fitKey={playbackTrip ? String(playbackTrip._id) : 'live'}
        height={400}
      />

      {playbackTrip && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`Playback: ${playbackTrip.tripNumber}`} onDelete={() => setPlaybackTrip(null)} />
          <Typography variant="caption" color="text.secondary">
            {playbackTrip.distanceKm as number} km · {playbackTrip.travelMinutes as number} min
          </Typography>
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
        <Tab label="Trip History" />
        <Tab label="Location History" />
        <Tab label="Device Info" />
      </Tabs>
      <Divider />

      {tab === 0 && (
        <DataTable
          columns={[
            { field: 'tripNumber', header: 'Trip #' },
            { field: 'startAddress', header: 'Start' },
            { field: 'endAddress', header: 'End' },
            { field: 'distanceKm', header: 'Distance (km)' },
            { field: 'travelMinutes', header: 'Travel (min)' },
            { field: 'idleMinutes', header: 'Idle (min)' },
            { field: 'status', header: 'Status' },
            {
              field: '_actions',
              header: '',
              render: (row: Record<string, unknown>) => (
                <Button size="small" onClick={() => handlePlayback(String(row._id))}>Playback</Button>
              ),
            },
          ]}
          rows={trips}
          loading={loading}
          search={search}
          onSearchChange={setSearch}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          getRowId={(row) => String(row._id)}
          selectable={false}
          emptyMessage="No GPS trips recorded yet."
        />
      )}

      {tab === 1 && (
        <DataTable
          columns={[
            { field: 'address', header: 'Location' },
            { field: 'latitude', header: 'Lat' },
            { field: 'longitude', header: 'Lng' },
            { field: 'speed', header: 'Speed' },
            { field: 'movementStatus', header: 'Status' },
            {
              field: 'recordedAt',
              header: 'Time',
              render: (row: Record<string, unknown>) => formatDate(String(row.recordedAt), { dateStyle: 'medium', timeStyle: 'short' }),
            },
          ]}
          rows={history}
          loading={loading}
          search={search}
          onSearchChange={setSearch}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          getRowId={(row) => String(row._id)}
          selectable={false}
        />
      )}

      {tab === 2 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Grid container spacing={2}>
            {([
              ['Device ID', device?.deviceId],
              ['IMEI', device?.imeiNumber],
              ['SIM', device?.simNumber],
              ['Provider', device?.provider],
              ['Type', device?.deviceType],
              ['Installation', device?.installationDate ? formatDate(String(device.installationDate)) : '—'],
              ['Last Seen', device?.lastSeenAt ? formatDate(String(device.lastSeenAt), { dateStyle: 'medium', timeStyle: 'short' }) : '—'],
              ['Ignition Support', device?.supportsIgnition ? 'Yes' : 'No'],
            ] as [string, unknown][]).map(([label, value]) => (
              <Grid item xs={12} sm={6} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2">{String(value || '—')}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};
